import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Box, Typography, Slider, Loader, Stepper, Tabs } from '@lucid/lego';
import type { Step } from '@lucid/lego';
import {
  useGetProspectById,
  useUpdateProspectStatus,
} from '../../../../api/services/lupp/prospect/prospect';
import { useStore } from '../../../../store/store';
import { getStepperSteps } from './utils';
import QuotationsTab from './QuotationsTab/QuotationsTab';
import DetailsTab from './DetailsTab/DetailsTab';
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal';
import DocumentsTab from './DocumentsTab/DocumentsTab';
import Modal from './Modal/Modal';
import type { StatusUpdatePayload } from './Modal/Modal';

type DetailsProps = {
  theme: 'light' | 'dark';
  isOpen: boolean;
  onClose: () => void;
  /** Prospect ID to fetch and display */
  prospectId: string | null;
  labels: Record<string, string>;
  onCreateQuotation: () => void;
  /** Controlled active tab ID */
  activeTab?: string;
  /** Callback when tab changes */
  onTabChange?: (tabId: string) => void;
  /** Callback when a quote is clicked */
  onQuoteClick?: (quoteId: string) => void;
};

/**
 * Prospect Details slider component
 * @component
 * @param props - Component props
 * @param props.theme - Theme for the component
 * @param props.isOpen - Whether the slider is open
 * @param props.onClose - Handler for closing the slider
 * @param props.prospectId - ID of the prospect to display
 * @param props.labels - Labels for the component
 * @param props.onCreateQuotation - Handler for create quotation button click
 * @returns JSX.Element
 */
const Details: React.FC<DetailsProps> = ({
  theme,
  isOpen,
  onClose,
  prospectId,
  labels,
  onCreateQuotation,
  activeTab: controlledActiveTab,
  onTabChange: onTabChangeProp,
  onQuoteClick,
}) => {
  const locale = useStore((state) => state.locale);
  const language = useStore((state) => state.language);
  const [dirtyTabs, setDirtyTabs] = useState<Record<string, boolean>>({});
  const [internalActiveTab, setInternalActiveTab] = useState('details');
  const [pendingTabId, setPendingTabId] = useState<string | null>(null);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [isStatusUpdateModalOpen, setIsStatusUpdateModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  // Derive activeTab - use controlled value if provided, otherwise internal state
  const activeTab = controlledActiveTab ?? internalActiveTab;

  // Reset dirty state when slider opens/closes or prospect changes
  useEffect(() => {
    if (isOpen) {
      setDirtyTabs({});
      if (!controlledActiveTab) {
        setInternalActiveTab('details');
      }
    }
  }, [isOpen, prospectId, controlledActiveTab]);

  const {
    data: prospectData,
    isLoading,
    error,
    refetch: refetchProspect,
  } = useGetProspectById(String(prospectId), !!prospectId && isOpen);

  // Derive prospect name from API response
  const prospectName = useMemo(() => {
    const details = prospectData?.prospectDetails;
    if (details?.prospectFirstName || details?.prospectLastName) {
      return `${details.prospectFirstName || ''} ${details.prospectLastName || ''}`.trim();
    }
    return '';
  }, [prospectData?.prospectDetails]);

  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  const { mutate: updateProspectStatus, isPending: isUpdatingStatus } = useUpdateProspectStatus();

  const stepperSteps = useMemo<Step[]>(() => {
    const steps = getStepperSteps(prospectData?.prospectDetails?.statusName, labels);
    return steps;
  }, [prospectData?.prospectDetails?.statusName, labels]);

  /**
   * Check if the current active tab has unsaved changes
   */
  const isCurrentTabDirty = useMemo(() => {
    return dirtyTabs[activeTab] || false;
  }, [dirtyTabs, activeTab]);

  /**
   * Handle form dirty state change for Details tab
   */
  const handleDetailsDirtyChange = useCallback((isDirty: boolean) => {
    setDirtyTabs((prev) => ({ ...prev, details: isDirty }));
  }, []);

  /**
   * Handle form dirty state change for Test Drive tab
   */
  const handleTestDriveDirtyChange = useCallback((isDirty: boolean) => {
    setDirtyTabs((prev) => ({ ...prev, 'test-drive': isDirty }));
  }, []);

  /**
   * Handle tab change with unsaved changes check
   */
  const handleTabChange = useCallback(
    (tabId: string) => {
      if (isCurrentTabDirty && tabId !== activeTab) {
        setPendingTabId(tabId);
        setIsUnsavedChangesModalOpen(true);
      } else {
        // Clear the target tab's dirty state since form will reinitialize
        setDirtyTabs((prev) => ({ ...prev, [tabId]: false }));
        if (onTabChangeProp) {
          onTabChangeProp(tabId);
        } else {
          setInternalActiveTab(tabId);
        }
      }
    },
    [isCurrentTabDirty, activeTab, onTabChangeProp]
  );

  /**
   * Handle stepper step click
   */
  const handleStepClick = useCallback((step: Step) => {
    // Don't open modal if step is disabled
    if (step.disabled) {
      return;
    }

    // Use step.value (English key) for status matching, not translated label
    const stepValue = (step as Step & { value?: string }).value || step.label || '';

    // Open status update modal for Contacting, Engaged, and Closed steps
    if (stepValue === 'Closed' || stepValue === 'Contacting' || stepValue === 'Engaged') {
      setSelectedStatus(stepValue);
      setIsStatusUpdateModalOpen(true);
    }
  }, []);

  /**
   * Handle status update save - handles both engagement and closed status payloads
   */
  const handleStatusUpdateSave = useCallback(
    (data: StatusUpdatePayload) => {
      if (!prospectId) return;

      if (data.type === 'engagement') {
        updateProspectStatus(
          {
            id: prospectId,
            data: {
              prospectIdentifier: prospectId,
              status: selectedStatus,
              nextEngagementDate: data.nextEngagementDate,
              notes: data.notes,
            },
          },
          {
            onSuccess: () => {
              refetchProspect();
              setIsStatusUpdateModalOpen(false);
            },
            onError: (error) => {
              console.error('Failed to update status:', error);
            },
          }
        );
      } else if (data.type === 'closed') {
        updateProspectStatus(
          {
            id: prospectId,
            data: {
              prospectIdentifier: prospectId,
              status: 'Closed',
              subStatus: data.subStatus,
              primaryCloseReason: data.primaryCloseReason,
              secondaryCloseReason: data.secondaryCloseReason,
            },
          },
          {
            onSuccess: () => {
              refetchProspect();
              setIsStatusUpdateModalOpen(false);
            },
            onError: (error) => {
              console.error('Failed to update closed status:', error);
            },
          }
        );
      }
    },
    [selectedStatus, prospectId, updateProspectStatus, refetchProspect]
  );

  /**
   * Handle cancel button click
   */
  const handleCancel = useCallback(() => {
    if (isCurrentTabDirty) {
      setPendingTabId(null);
      setIsUnsavedChangesModalOpen(true);
    } else {
      onClose();
    }
  }, [isCurrentTabDirty, onClose]);

  /**
   * Handle stay on page (close modal)
   */
  const handleStayOnPage = useCallback(() => {
    setIsUnsavedChangesModalOpen(false);
    setPendingTabId(null);
  }, []);

  /**
   * Handle continue (discard changes)
   */
  const handleContinue = useCallback(() => {
    setIsUnsavedChangesModalOpen(false);
    // Clear dirty state for current tab when discarding changes
    setDirtyTabs((prev) => ({ ...prev, [activeTab]: false }));
    if (pendingTabId) {
      if (onTabChangeProp) {
        onTabChangeProp(pendingTabId);
      } else {
        setInternalActiveTab(pendingTabId);
      }
      setPendingTabId(null);
    } else {
      onClose();
    }
  }, [pendingTabId, onClose, activeTab, onTabChangeProp]);

  /**
   * Render loading state
   */
  const renderLoading = () => (
    <Box pt={48} pb={48} className="fetchingOverlay">
      <Loader size={48} theme={invertedTheme} />
    </Box>
  );

  /**
   * Render error state
   */
  const renderError = () => (
    <Box pt={48} pb={48}>
      <Typography variant="body" size="large" theme={invertedTheme} color="error-1">
        {labels?.['details.loadError'] ?? 'Error loading prospect details. Please try again.'}
      </Typography>
    </Box>
  );

  /**
   * Render empty state
   */
  const renderEmpty = () => (
    <Box>
      <Typography variant="body" size="small" theme={invertedTheme} color="white">
        {labels.loading_prospect_details}
      </Typography>
    </Box>
  );

  /**
   * Render content with stepper and tabs
   */
  const renderContent = () => (
    <Box>
      {/* Status Stepper */}
      <Box mb={32}>
        <Stepper
          steps={stepperSteps}
          theme={invertedTheme}
          dataTestId="prospect-status-stepper"
          interactive={true}
          onStepClick={handleStepClick}
        />
      </Box>

      {/* Tabs */}
      <Tabs
        dataTestId="prospect-details-tabs"
        theme={invertedTheme}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabs={[
          {
            id: 'details',
            label: labels?.['details.details'] ?? 'Details',
            content: (
              <DetailsTab
                key={`details-${prospectData?.prospectDetails?.prospectIdentifier}`}
                prospect={prospectData?.prospectDetails}
                onDirtyChange={handleDetailsDirtyChange}
                onCancel={handleCancel}
                labels={labels}
              />
            ),
          },
          {
            id: 'test-drive',
            label: labels?.['details.testDrive'] ?? 'Test Drive',
            content: (
              <DetailsTab
                key={`test-drive-${prospectData?.prospectDetails?.prospectIdentifier}`}
                prospect={prospectData?.prospectDetails}
                onDirtyChange={handleTestDriveDirtyChange}
                onCancel={handleCancel}
                isTestDriveTab
                labels={labels}
              />
            ),
          },
          {
            id: 'quotations',
            label: labels?.['details.quotations'] ?? 'Quotations',
            content: (
              <QuotationsTab
                theme={invertedTheme}
                onCreateQuotation={onCreateQuotation}
                prospectId={prospectId || ''}
                quoteList={prospectData?.quoteList || []}
                labels={labels}
                onQuoteClick={onQuoteClick || (() => {})}
                locale={locale}
              />
            ),
          },
          {
            id: 'documents',
            label: labels?.['details.documents'] ?? 'Documents',
            content: (
              <DocumentsTab
                theme={invertedTheme}
                prospectId={prospectId || ''}
                prospectFilesMetadata={prospectData?.prospectDetails?.prospectFilesMetadata}
                labels={labels}
              />
            ),
          },
        ]}
      />
    </Box>
  );

  return (
    <>
      <Slider
        isOpen={isOpen}
        onClose={handleCancel}
        title={prospectName}
        width="1000px"
        direction="right"
        theme={invertedTheme}
      >
        {isLoading && renderLoading()}
        {error && renderError()}
        {!isLoading && !error && prospectData && renderContent()}
        {!isLoading && !error && !prospectData && renderEmpty()}
      </Slider>

      <ConfirmationModal
        dataTestId="unsaved-changes-modal"
        isOpen={isUnsavedChangesModalOpen}
        onClose={handleStayOnPage}
        title={labels?.['unsavedChanges.title'] ?? 'Save Changes'}
        message={
          <>
            {labels?.['unsavedChanges.message'] ??
              'It looks like you have unsaved changes. Please save before you move on.'}
          </>
        }
        theme={invertedTheme}
        cancelLabel={labels?.['unsavedChanges.stayButton'] ?? 'STAY ON THIS PAGE'}
        confirmLabel={labels?.['unsavedChanges.continueButton'] ?? 'CONTINUE'}
        onCancel={handleStayOnPage}
        onConfirm={handleContinue}
      />

      <Modal
        isOpen={isStatusUpdateModalOpen}
        onClose={() => setIsStatusUpdateModalOpen(false)}
        updatingToStatus={selectedStatus}
        initialNextEngagementDate={prospectData?.prospectDetails?.nextEngagementDate || ''}
        theme={invertedTheme}
        onSave={handleStatusUpdateSave}
        isLoading={isUpdatingStatus}
        locale={locale}
        language={language ?? locale.split('-')[0]}
        labels={labels}
      />
    </>
  );
};

export default Details;
